import type { SectionLayout } from '@flame/shared';
import { useState } from 'preact/hooks';
import { Api, type Section } from '../api';
import { attempt, loadPage } from '../state';
import { Field, Modal } from './ui';

export function SectionEditor({ section, onClose }: { section?: Section; onClose: () => void }) {
  const [name, setName] = useState(section?.name ?? '');
  const [layout, setLayout] = useState<SectionLayout>(section?.layout ?? 'list');
  const [pinned, setPinned] = useState(section?.pinned ?? true);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    const body = { name, layout, pinned };
    const ok = await attempt(() =>
      section ? Api.updateSection(section.id, body) : Api.createSection(body)
    );
    setBusy(false);
    if (ok) {
      await loadPage();
      onClose();
    }
  }

  async function remove() {
    if (!section) return;
    const count = section.links.length;
    const question = count
      ? `Delete "${section.name}" and its ${count} link${count === 1 ? '' : 's'}?`
      : `Delete "${section.name}"?`;
    if (!confirm(question)) return;
    const ok = await attempt(() => Api.deleteSection(section.id, count > 0));
    if (ok) {
      await loadPage();
      onClose();
    }
  }

  return (
    <Modal title={section ? 'Edit section' : 'Add section'} onClose={onClose}>
      <form class="form" onSubmit={submit}>
        <Field label="Name">
          <input
            required
            autoFocus
            maxLength={64}
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        <fieldset class="segmented">
          <legend class="field-label">Layout</legend>
          {(['list', 'tiles'] as const).map((l) => (
            <label key={l}>
              <input
                type="radio"
                name="layout"
                checked={layout === l}
                onChange={() => setLayout(l)}
              />
              {l === 'list' ? 'List (one column)' : 'Tiles (full width)'}
            </label>
          ))}
        </fieldset>
        <label class="check">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.currentTarget.checked)} />
          Show on the start page (unchecked sections are hidden until you show them)
        </label>
        <footer class="form-actions">
          {section && (
            <button type="button" class="button button-danger" onClick={remove}>
              Delete
            </button>
          )}
          <span class="spacer" />
          <button type="button" class="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="button button-primary" disabled={busy}>
            {section ? 'Save' : 'Add section'}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
